import type { CSSProperties } from 'react'

// Bulk edit look. Editable cells are filled and bordered across the whole cell
// so they read as type-here; read-only cells stay plain with dimmed text so the
// editable ones stand out. The border is an inset shadow rather than a real
// border, which would shift the cell layout.
// Light uses a tint of the cisgenx brand green (#004744); dark uses a lighter
// teal that reads on the dark surface.
const CELL_COLORS = {
  light: {
    editableFill: 'rgba(0, 71, 68, 0.07)',
    editableBorder: 'inset 0 0 0 1px rgba(0, 71, 68, 0.35)',
    readonlyText: '#8a9099',
    pendingFill: 'rgba(245, 158, 11, 0.18)',
    pendingBorder: 'inset 0 0 0 1px rgba(217, 119, 6, 0.6)',
  },
  dark: {
    // Emerald reads as a clean green accent against the teal-dark surface;
    // orange keeps the "changed" state clearly warm instead of muddy brown.
    editableFill: 'rgba(16, 185, 129, 0.15)',
    editableBorder: 'inset 0 0 0 1px rgba(52, 211, 153, 0.55)',
    readonlyText: '#7d8894',
    // Higher-alpha amber: a faint warm overlay just muddies to brown over the
    // dark-teal row, so it needs to be strong enough to read as amber.
    pendingFill: 'rgba(245, 158, 11, 0.4)',
    pendingBorder: 'inset 0 0 0 1px rgba(253, 186, 46, 0.9)',
  },
}

/**
 * Cell style for bulk edit. Editable cells get a filled, bordered look across
 * the whole cell; once changed and pending save they turn amber; read-only
 * cells are dimmed so the editable ones stand out. Outside bulk edit, and on
 * rows that carry their own look, cells stay plain.
 *
 * `plain` covers the create chip row and staged drafts: a draft takes the
 * whole-row amber `new` tint, so laying the per-cell editable fill over it
 * would blur two signals into one — and an inline background would also win
 * over the invalid class a refused save marks the cell with.
 */
export const editableCellStyle = ({
  bulkEditMode,
  isEditable,
  plain,
  isEdited,
  isDark,
}: {
  bulkEditMode: boolean
  isEditable: boolean
  plain: boolean
  isEdited: boolean
  isDark: boolean
}): CSSProperties => {
  const base: CSSProperties = { display: 'flex', alignItems: 'center' }
  if (!bulkEditMode || plain) return base

  const c = isDark ? CELL_COLORS.dark : CELL_COLORS.light
  if (isEditable) {
    base.backgroundColor = isEdited ? c.pendingFill : c.editableFill
    base.boxShadow = isEdited ? c.pendingBorder : c.editableBorder
    base.cursor = 'text'
  } else {
    base.color = c.readonlyText
  }
  return base
}

// Soft green pill for the in-cell link actions (Flow Rule, Fertigation), per
// theme: brand green on light, a lighter teal that reads on the dark surface.
export const brandLinkChip = (isDark: boolean): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: 28,
  padding: '0 12px',
  borderRadius: 8,
  backgroundColor: isDark ? 'rgba(16, 185, 129, 0.13)' : '#eef4f3',
  border: `1px solid ${isDark ? 'rgba(52, 211, 153, 0.4)' : '#dce8e5'}`,
  color: isDark ? '#5fe0ad' : '#004744',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
})
